import type { KeyboardEvent, ReactNode } from 'react';
import { Button, Form, Spinner, Table } from 'react-bootstrap';
import { Pencil } from 'react-bootstrap-icons';
import { Client, getAccountOrgBadgeInfo } from '@/types';
import type { ClientMetadata, AccountOrgValue } from '@/types';
import { ClientTags } from '@/components/clientFlags/ClientTags';
import { useClientFlags } from '@/hooks/useClientFlags';
import { groupClientsByType } from '@/services/clientService';
import './ClientTableGroup.css';

interface ClientTableGroupProps {
  clients: Client[];
  selectedClient?: Client | null;
  onSelectClient?: (client: Client) => void;
  onUpdateClient?: (client: Client) => void;
  searchTerm?: string;
  metadataMap?: ReadonlyMap<string, ClientMetadata>;
  orgMap?: Map<string, AccountOrgValue | null>;
  /** The client list is still on its way. */
  loading?: boolean;
  /**
   * The clients ticked for a change to many at once. With this and
   * `onTickedChange` set, each row gets a tick box and the header ticks them all.
   */
  tickedNames?: ReadonlySet<string>;
  onTickedChange?: (clientNames: string[], ticked: boolean) => void;
  /** Shown in place of "No client matches" when the list is empty. */
  emptyContent?: ReactNode;
}

/** A status pill is loud on a long list, so no status gets the solid purple or the black one. */

/** The clients as one table, customers first, then dev and demo. A click on a row selects it. */
export function ClientTableGroup({
  clients,
  selectedClient,
  onSelectClient,
  onUpdateClient,
  searchTerm = '',
  metadataMap,
  orgMap,
  loading = false,
  tickedNames,
  onTickedChange,
  emptyContent,
}: ClientTableGroupProps) {
  const flags = useClientFlags();
  // Filter clients by search term if provided
  const filteredClients = searchTerm
    ? clients.filter((c) => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : clients;

  const { devClients, productionClients } = groupClientsByType(filteredClients);
  const tickable = Boolean(tickedNames && onTickedChange);
  const columnCount = 3 + (onUpdateClient ? 1 : 0) + (tickable ? 1 : 0);
  const listedNames = [...productionClients, ...devClients].map((c) => c.name);
  const tickedListed = listedNames.filter((name) => tickedNames?.has(name));
  const allTicked = listedNames.length > 0 && tickedListed.length === listedNames.length;

  const renderClientRow = (client: Client) => {
    const org = getAccountOrgBadgeInfo(orgMap?.get(client.name) ?? metadataMap?.get(client.name)?.accountOrg ?? null);
    const isSelected = selectedClient?.name === client.name;
    const isTicked = Boolean(tickedNames?.has(client.name));
    const selectOnKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
      // Only a key pressed on the row itself selects it, not one pressed on the Edit button inside.
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectClient?.(client);
      }
    };
    return (
      <tr
        key={client.name}
        className={`client-table__row${isSelected ? ' is-selected' : ''}${isTicked ? ' is-ticked' : ''}`}
        aria-current={isSelected ? 'true' : undefined}
        tabIndex={onSelectClient ? 0 : undefined}
        onClick={() => onSelectClient?.(client)}
        onKeyDown={selectOnKey}
      >
        {tickable && (
          // The tick box changes the tick only. It never opens the client.
          <td className="client-table__tick" onClick={(e) => e.stopPropagation()}>
            <Form.Check
              type="checkbox"
              aria-label={`Select ${client.name}`}
              checked={isTicked}
              onChange={(e) => onTickedChange?.([client.name], e.currentTarget.checked)}
            />
          </td>
        )}
        <td className="client-table__name" title={client.name}>
          <span className="client-table__name-text">{client.name}</span>
          <ClientTags clientName={client.name} flags={flags} config={client.config} />
        </td>
        <td className="client-table__org">{org?.label ?? ''}</td>
        <td className="mono">{client.config.region}</td>
        {onUpdateClient && (
          <td className="client-table__action">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={(e) => {
                e.stopPropagation();
                onUpdateClient(client);
              }}
            >
              <Pencil size={12} className="me-1" />
              Edit
            </Button>
          </td>
        )}
      </tr>
    );
  };

  const renderSectionHeader = (title: string, count: number) => (
    <tr className="client-table__section">
      <th colSpan={columnCount} scope="colgroup">
        {title}
        <span className="client-table__section-count">{count}</span>
      </th>
    </tr>
  );

  const hasResults = devClients.length > 0 || productionClients.length > 0;

  return (
    <Table hover className="mb-0 client-table">
      <thead>
        <tr>
          {tickable && (
            <th className="client-table__tick">
              <Form.Check
                type="checkbox"
                aria-label="Select every client in the list"
                checked={allTicked}
                ref={(el: HTMLInputElement | null) => {
                  if (el) el.indeterminate = tickedListed.length > 0 && !allTicked;
                }}
                disabled={loading || listedNames.length === 0}
                onChange={(e) => onTickedChange?.(listedNames, e.currentTarget.checked)}
              />
            </th>
          )}
          <th>Client</th>
          <th>Organisation</th>
          <th>Region</th>
          {onUpdateClient && <th aria-label="Actions" />}
        </tr>
      </thead>
      <tbody>
        {loading && (
          <tr>
            <td colSpan={columnCount} className="client-table__empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading clients
            </td>
          </tr>
        )}

        {!loading && !hasResults && (
          <tr>
            <td colSpan={columnCount} className="client-table__empty">
              {emptyContent ?? 'No client matches.'}
            </td>
          </tr>
        )}

        {!loading && productionClients.length > 0 && (
          <>
            {renderSectionHeader('Customers', productionClients.length)}
            {productionClients.map(renderClientRow)}
          </>
        )}

        {!loading && devClients.length > 0 && (
          <>
            {renderSectionHeader('Dev and demo', devClients.length)}
            {devClients.map(renderClientRow)}
          </>
        )}
      </tbody>
    </Table>
  );
}
